/**
 * 智能体用户模板管理器
 * 负责管理智能体用户模板的创建、更新、删除等操作
 * @author Joey
 */

import { useI18nNavigate } from "@/hooks/use-i18n-navigate";
import { useToast } from "@/hooks/use-toast";
import { agentV2SaveTemplate, AgentV2SaveTemplateRequest } from "@/services/agent-v2-save-template";
import { agentV2StyleListLocal, StyleData } from "@/services/agent-v2-style-list-local";
import { AgentTemplatesData, agentV2TemplatesList } from "@/services/agent-v2-template-list";
import { canUseWindow } from "@/utils/canuseWindow";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PUSH_SETTINGS_TYPE, SETTING_TABS, userManagementManager } from "./agent-dashboard-user-management-manager";
import { agentV2DeleteTemplate } from "@/services/agent-v2-delete-template";
import { agentV2TemplateDetail, AgentV2TemplateDetailData } from "@/services/agent-v2-template-detail";
import { agentV2UpdateTemplate, AgentV2UpdateTemplateRequest } from "@/services/agent-v2-update-template";
import { agentV2LarkImageTableData, LarkImageTableItemsData } from "@/services/agent-v2-feishu-tuwen-table-data";
import { agentV2LarkArticleTable, LarkArticleTableItemsData } from "@/services/agent-v2-feishu-table-data";
import { useTranslation } from "react-i18next";
import { iamAccountHooks } from "./iam-account-manager";

export interface TemplateSearchForm {
  type: number;
  page: number;
  pageSize: number;
  total?: number;
}

export interface StyleSearchForm {
  page: number;
  pageSize: number;
  total?: number;
  styleId: string;
}

export interface LarkImageTemplateSearchForm {
  pageToken: string;
  pageSize: string;
  total?: number;
  templateId?: string;
}

export interface LarkArticleTemplateSearchForm {
  pageToken: string;
  pageSize: string;
  total?: number;
  templateId?: string;
}

type TemplateListChangeEventDetail = {
  templateList: AgentTemplatesData[];
};
type TemplateListChangeEvent = CustomEvent<TemplateListChangeEventDetail>;

type StyleListChangeEventDetail = { styleList: StyleData[] };
type StyleListChangeEvent = CustomEvent<StyleListChangeEventDetail>;

type StyleAllListChangeEventDetail = { styleAllList: StyleData[] };
type StyleAllListChangeEvent = CustomEvent<StyleAllListChangeEventDetail>;

type TemplateSearchFormChangeEventDetail = TemplateSearchForm;
type TemplateSearchFormChangeEvent = CustomEvent<TemplateSearchFormChangeEventDetail>;

type StyleSearchFormChangeEventDetail = StyleSearchForm;
type StyleSearchFormChangeEvent = CustomEvent<StyleSearchFormChangeEventDetail>;

type StyleAllSearchFormChangeEventDetail = StyleSearchForm;
type StyleAllSearchFormChangeEvent = CustomEvent<StyleAllSearchFormChangeEventDetail>;

type TemplateDetailChangeEventDetail = AgentV2TemplateDetailData;
type TemplateDetailChangeEvent = CustomEvent<TemplateDetailChangeEventDetail>;

type LarkImageTemplatesChangeEventDetail = LarkImageTableItemsData[];
type LarkImageTemplatesChangeEvent = CustomEvent<LarkImageTemplatesChangeEventDetail>;

type LarkImageTemplateSearchChangeEventDetail = LarkImageTemplateSearchForm;
type LarkImageTemplateSearchChangeEvent = CustomEvent<LarkImageTemplateSearchChangeEventDetail>;

type LarkArticleTemplatesChangeEventDetail = LarkArticleTableItemsData[];
type LarkArticleTemplatesChangeEvent = CustomEvent<LarkArticleTemplatesChangeEventDetail>;

type LarkArticleTemplateSearchChangeEventDetail = LarkArticleTemplateSearchForm;
type LarkArticleTemplateSearchChangeEvent = CustomEvent<LarkArticleTemplateSearchChangeEventDetail>;

export class AgentTemplateManager {
  private static instance: AgentTemplateManager;
  private readonly STORAGE_KEY = "MIUTA_AGENT_TEMPLATE";
  // 当前智能体ID
  private agentId: string = "";
  // 当前用户ID
  private userId: string = "";
  // 模板列表
  private templateList: AgentTemplatesData[] = [];
  // 模板详情
  private templateDetail: AgentV2TemplateDetailData = {} as AgentV2TemplateDetailData;
  // 样式列表
  private styleList: StyleData[] = [];
  private styleAllList: StyleData[] = [];
  // 搜索表单的数据
  private templateSearchForm: TemplateSearchForm = {
    type: 1,
    page: 1,
    pageSize: 1000,
    total: 0,
  }
  // 样式搜索表单的数据
  private styleSearchForm: StyleSearchForm = {
    page: 1,
    pageSize: 20,
    total: 0,
    styleId: "",
  }
  // 样式全部搜索表单的数据
  private styleAllSearchForm: StyleSearchForm = {
    page: 1,
    pageSize: 1000,
    total: 0,
    styleId: "",
  }
  // 图文-飞书模板列表
  private LarkImageTemplates: LarkImageTableItemsData[] = [];
  private LarkImageTemplateSearchForm: LarkImageTemplateSearchForm = {
    pageToken: '',
    pageSize: '100',
    total: 0,
  };
  // 文章-飞书模板列表
  private LarkArticleTemplates: LarkArticleTableItemsData[] = [];
  private LarkArticleTemplateSearchForm: LarkArticleTemplateSearchForm = {
    pageToken: '',
    pageSize: '100',
    total: 0,
  };

  public constructor() { }

  // 单例模式
  public static getInstance(): AgentTemplateManager {
    if (!AgentTemplateManager.instance) {
      AgentTemplateManager.instance = new AgentTemplateManager();
    }
    return AgentTemplateManager.instance;
  }

  // 初始化
  public async init(agentId: string, userId: string) {
    this.setAgentId(agentId);
    this.setUserId(userId);
    try {
      // 初始化时，如果列表为空，才请求数据
      if (this.getTemplateList().length === 0) {
        await this.fetchTemplateList({ userId, agentId });
      }
    } catch (error) {
      console.error("Failed to init agent template list cache:", error);
      throw error;
    }
  }

  // 请求模板列表数据
  public async fetchTemplateList({
    userId,
    agentId,
    page,
    type,
  }: {
    userId?: string,
    agentId?: string,
    page?: number,
    type?: number,
  }) {
    try {
      const data = await agentV2TemplatesList({
        ...this.getTemplateSearchForm(),
        userId: userId || this.getUserId(),
        agentId: agentId || this.getAgentId(),
        page: page ?? this.templateSearchForm.page,
        type: type ?? this.templateSearchForm.type,
      });
      this.setTemplateList(data.list);
      this.setTemplateSearchForm({
        ...this.templateSearchForm,
        page: data.page,
        pageSize: data.pageSize,
        total: data.total,
        type: type ?? this.templateSearchForm.type,
      });
    } catch (error) {
      console.error("Failed to fetch agent content list:", error);
      this.dispatchTemplateListChange();
      throw error;
    }
  }

  // 请求样式列表数据
  public async fetchStyleList(page?: number, externalParams?: object) {
    try {
      const data = await agentV2StyleListLocal({
        ...this.getStyleSearchForm(),
        page: page ?? this.styleSearchForm.page,
        ...externalParams,
      });

      this.setStyleList(data.results);
      this.setStyleSearchForm({
        ...this.styleSearchForm,
        page: data.page,
        pageSize: data.pageSize,
        total: data.total,
      });
    } catch (error) {
      console.error("Failed to fetch agent style list:", error);
      throw error;
    }
  }

  // 请求所有样式列表数据
  public async fetchStyleAllList(page?: number, externalParams?: object) {
    try {
      const data = await agentV2StyleListLocal({
        ...this.getStyleAllSearchForm(),
        page: page ?? this.styleAllSearchForm.page,
        ...externalParams,
      });

      const currentPage = page ?? this.styleAllSearchForm.page;

      if (currentPage === 1) {
        // 新搜索或第一页：直接替换（防止后端返回空时保留旧数据）
        this.setStyleAllList([...data.results]);
      } else {
        // 翻页：追加
        this.setStyleAllList([...this.getStyleAllList(), ...data.results]);
      }

      this.setStyleAllSearchForm({
        ...this.styleAllSearchForm,
        page: data.page,
        pageSize: data.pageSize,
        total: data.total,
      });
    } catch (error) {
      console.error("Failed to fetch agent style list:", error);
      throw error;
    }
  }

  // 保存模板
  public async saveTemplate(
    data: | Omit<AgentV2SaveTemplateRequest, 'agentId'>[],
    agentId?: string,
  ) {
    try {
      const buildParams = data.map((item) => ({
        ...item,
        agentId: agentId || this.getAgentId(),
      }));
      await agentV2SaveTemplate(buildParams);
    } catch (error) {
      console.error("Failed to save agent template:", error);
      throw error;
    }
  }

  // 更新模板
  public async updateTemplate({
    uuid,
    userId,
    type,
    styleId,
    coverUrl,
    coverVideo,
    actorModelId,
    voiceModelId,
    speedRatio,
    templateName,
  }: Omit<AgentV2UpdateTemplateRequest, 'agentId'>, agentId?: string) {
    try {
      await agentV2UpdateTemplate({
        agentId: agentId || this.getAgentId(),
        userId: userId || this.getUserId(),
        uuid,
        styleId,
        coverVideo,
        coverUrl,
        type,
        actorModelId,
        voiceModelId,
        speedRatio,
        templateName,
      });
    } catch (error) {
      console.error("Failed to update agent template:", error);
      throw error;
    }
  }

  // 请求模板详情数据
  public async fetchTemplateDetail({ uuid }: { uuid: string }) {
    try {
      const data = await agentV2TemplateDetail({
        uuid,
      });
      this.setTemplateDetail(data);
      return data;
    } catch (error) {
      console.error("Failed to fetch agent template detail:", error);
      throw error;
    }
  }

  // 删除模板
  public async deleteTemplate({ templateId, userId }: { templateId: string; userId: string }, agentId?: string) {
    try {
      await agentV2DeleteTemplate({
        templateId,
        userId,
        agentId: agentId || this.getAgentId(),
      });
    } catch (error) {
      console.error("Failed to delete agent template:", error);
      throw error;
    }
  }

  // ========================== 图文模板 ==========================
  // 请求飞书模板列表数据（添加模板弹窗里面的列表数据）
  public async fetchLarkImageTemplates(externalParams?: object) {
    try {
      const data = await agentV2LarkImageTableData({
        ...this.getLarkImageTemplateSearchForm(),
        ...externalParams,
      });
      this.setLarkImageTemplates(data.data.items);
    } catch (error) {
      console.error("Failed to fetch agent lark image templates:", error);
      this.dispatchLarkImageTemplatesChange();
      throw error;
    }
  }

  // ========================== 文章模板 ==========================
  // 请求飞书模板列表数据（添加模板弹窗里面的列表数据）
  public async fetchLarkArticleTemplates(externalParams?: object) {
    try {
      const data = await agentV2LarkArticleTable({
        ...this.getLarkArticleTemplateSearchForm(),
        ...externalParams,
      });
      this.setLarkArticleTemplates(data.data.items);
    } catch (error) {
      console.error("Failed to fetch agent lark article templates:", error);
      this.dispatchLarkArticleTemplatesChange();
      throw error;
    }
  }

  // ==================== 读取写入操作 ====================
  public getAgentId() {
    return this.agentId;
  }
  public setAgentId(agentId: string) {
    this.agentId = agentId;
  }
  public getUserId() {
    return this.userId;
  }
  public setUserId(userId: string) {
    this.userId = userId;
  }

  public getTemplateList() {
    return this.templateList;
  }
  private setTemplateList(list: AgentTemplatesData[]) {
    this.templateList = list;
    this.dispatchTemplateListChange();
  }

  public getStyleList() {
    return this.styleList;
  }
  private setStyleList(list: StyleData[]) {
    this.styleList = list;
    this.dispatchStyleListChange();
  }

  public getStyleAllList() {
    return this.styleAllList;
  }
  private setStyleAllList(list: StyleData[]) {
    this.styleAllList = list;
    this.dispatchStyleAllListChange();
  }

  public getStyleAllSearchForm() {
    return this.styleAllSearchForm;
  }
  public setStyleAllSearchForm(form: StyleSearchForm) {
    this.styleAllSearchForm = form;
    this.dispatchStyleAllSearchFormChange();
  }

  public getTemplateSearchForm() {
    return this.templateSearchForm;
  }
  public setTemplateSearchForm(form: TemplateSearchForm) {
    this.templateSearchForm = form;
    this.dispatchTemplateSearchFormChange();
  }

  public getStyleSearchForm() {
    return this.styleSearchForm;
  }
  public setStyleSearchForm(form: StyleSearchForm) {
    this.styleSearchForm = form;
    this.dispatchStyleSearchFormChange();
  }

  public getTemplateDetail() {
    return this.templateDetail;
  }
  public setTemplateDetail(data: AgentV2TemplateDetailData) {
    this.templateDetail = data;
    this.dispatchTemplateDetailChange();
  }

  public getLarkImageTemplates() {
    return this.LarkImageTemplates;
  }
  private setLarkImageTemplates(list: LarkImageTableItemsData[]) {
    this.LarkImageTemplates = list;
    this.dispatchLarkImageTemplatesChange();
  }

  public getLarkImageTemplateSearchForm() {
    return this.LarkImageTemplateSearchForm;
  }
  public setLarkImageTemplateSearchForm(form: LarkImageTemplateSearchForm) {
    this.LarkImageTemplateSearchForm = form;
    this.dispatchLarkImageTemplateSearchChange();
  }

  public getLarkArticleTemplates() {
    return this.LarkArticleTemplates;
  }
  private setLarkArticleTemplates(list: LarkArticleTableItemsData[]) {
    this.LarkArticleTemplates = list;
    this.dispatchLarkArticleTemplatesChange();
  }

  public getLarkArticleTemplateSearchForm() {
    return this.LarkArticleTemplateSearchForm;
  }
  public setLarkArticleTemplateSearchForm(form: LarkArticleTemplateSearchForm) {
    this.LarkArticleTemplateSearchForm = form;
    this.dispatchLarkArticleTemplateSearchChange();
  }

  // ==================== 订阅通知操作 ====================
  // 监听模板列表变化事件
  private dispatchTemplateListChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<TemplateListChangeEventDetail>(`${this.STORAGE_KEY}:template-list`, {
      detail: { templateList: this.templateList }
    });
    window.dispatchEvent(event);
  }
  public onTemplateListChange(listener: (e: TemplateListChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:template-list`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听模板搜索表单变化事件
  private dispatchTemplateSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<TemplateSearchFormChangeEventDetail>(`${this.STORAGE_KEY}:template-search-form`, {
      detail: this.templateSearchForm
    });
    window.dispatchEvent(event);
  }
  public onTemplateSearchFormChange(listener: (e: TemplateSearchFormChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:template-search-form`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听样式列表变化事件
  private dispatchStyleListChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<StyleListChangeEventDetail>(`${this.STORAGE_KEY}:style-list`, {
      detail: { styleList: this.styleList }
    });
    window.dispatchEvent(event);
  }
  public onStyleListChange(listener: (e: StyleListChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:style-list`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听样式所有列表变化事件
  private dispatchStyleAllListChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<StyleAllListChangeEventDetail>(`${this.STORAGE_KEY}:style-all-list`, {
      detail: { styleAllList: this.styleAllList }
    });
    window.dispatchEvent(event);
  }
  public onStyleAllListChange(listener: (e: StyleAllListChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:style-all-list`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听样式搜索表单变化事件
  private dispatchStyleSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<StyleSearchFormChangeEventDetail>(`${this.STORAGE_KEY}:style-search-form`, {
      detail: this.styleSearchForm
    });
    window.dispatchEvent(event);
  }
  public onStyleSearchFormChange(listener: (e: StyleSearchFormChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:style-search-form`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听样式所有搜索表单变化事件
  private dispatchStyleAllSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<StyleAllSearchFormChangeEventDetail>(`${this.STORAGE_KEY}:style-all-search-form`, {
      detail: this.styleAllSearchForm
    });
    window.dispatchEvent(event);
  }
  public onStyleAllSearchFormChange(listener: (e: StyleAllSearchFormChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:style-all-search-form`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听模板详情变化事件
  private dispatchTemplateDetailChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<TemplateDetailChangeEventDetail>(`${this.STORAGE_KEY}:template-detail`, {
      detail: this.templateDetail
    });
    window.dispatchEvent(event);
  }
  public onTemplateDetailChange(listener: (e: TemplateDetailChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:template-detail`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 图文-飞书模板列表
  private dispatchLarkImageTemplatesChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<LarkImageTemplatesChangeEventDetail>(`${this.STORAGE_KEY}:lark-image-templates`, {
      detail: this.LarkImageTemplates
    });
    window.dispatchEvent(event);
  }
  public onLarkImageTemplatesChange(listener: (e: LarkImageTemplatesChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:lark-image-templates`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 图文-飞书模板列表搜索表单
  private dispatchLarkImageTemplateSearchChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<LarkImageTemplateSearchChangeEventDetail>(`${this.STORAGE_KEY}:lark-image-template-search-form`, {
      detail: this.LarkImageTemplateSearchForm
    });
    window.dispatchEvent(event);
  }
  public onLarkImageTemplateSearchChange(listener: (e: LarkImageTemplateSearchChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:lark-image-template-search-form`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 文章-飞书模板列表
  private dispatchLarkArticleTemplatesChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<LarkArticleTemplatesChangeEventDetail>(`${this.STORAGE_KEY}:lark-article-templates`, {
      detail: this.LarkArticleTemplates
    });
    window.dispatchEvent(event);
  }
  public onLarkArticleTemplatesChange(listener: (e: LarkArticleTemplatesChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:lark-article-templates`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 文章-飞书模板列表搜索表单
  private dispatchLarkArticleTemplateSearchChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<LarkArticleTemplateSearchChangeEventDetail>(`${this.STORAGE_KEY}:lark-article-template-search-form`, {
      detail: this.LarkArticleTemplateSearchForm
    });
    window.dispatchEvent(event);
  }
  public onLarkArticleTemplateSearchChange(listener: (e: LarkArticleTemplateSearchChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:lark-article-template-search-form`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }
}

// ============= Hooks =============
//========================= 视频模板 =============================
// 模板列表数据
const useTemplateListData = () => {
  const toast = useToast();
  const { id } = useParams();
  const _userId = new URLSearchParams(location.search).get('userId');


  const [templateList, setTemplateList] = useState(agentTemplateManager.getTemplateList());
  const [templateSearchFormData, setTemplateSearchFormData] = useState(agentTemplateManager.getTemplateSearchForm());
  const [loading, setLoading] = useState(false);

  const init = async () => {
    if (!id || !_userId) {
      console.error('Agent id or user id is required', { id, _userId });
      return;
    }
    agentTemplateManager.setAgentId(id);
    agentTemplateManager.setUserId(_userId);
  }

  const search = async (page?: number, type?: number) => {
    setLoading(true);
    try {
      await agentTemplateManager.fetchTemplateList({
        type,
        userId: _userId,
        agentId: id,
        page,
      });
    } catch (error) {
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const changeSearchForm = (key: keyof TemplateSearchForm, value: string | number) => {
    agentTemplateManager.setTemplateSearchForm({
      ...templateSearchFormData,
      [key]: value,
    });
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const unsubscribe = agentTemplateManager.onTemplateListChange((e) => {
      setTemplateList(e.detail.templateList)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = agentTemplateManager.onTemplateSearchFormChange((e) => {
      setTemplateSearchFormData(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    loading,
    templateList,
    templateSearchFormData,

    changeSearchForm,
    search,
  }
}

// 样式列表数据
const useStyleListData = (externalParams?: object) => {
  const toast = useToast();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);

  const [styleList, setStyleList] = useState(agentTemplateManager.getStyleList());
  const [styleSearchFormData, setStyleSearchFormData] = useState(agentTemplateManager.getStyleSearchForm());

  const search = async (page?: number) => {
    // if (page === 1) setStyleList([]);
    setLoading(true);
    try {
      await agentTemplateManager.fetchStyleList(page, externalParams);
    } catch (error) {
      toast.showToastByCode(error);
    }
    setLoading(false);
  }

  const changeSearchForm = (key: keyof StyleSearchForm, value: string | number) => {
    agentTemplateManager.setStyleSearchForm({
      ...styleSearchFormData,
      [key]: value,
    });
  }

  const changePage = (page: number) => {
    changeSearchForm('page', page);
  }

  const resetAndSearch = async (isSearch: boolean) => {
    agentTemplateManager.setStyleSearchForm({
      page: 1,
      pageSize: 10,
      total: 0,
      styleId: "",
    });
    setStyleList([]);
    isSearch && await search();
  }

  const addStyleList = (style: Omit<StyleData, 'id'> | Omit<StyleData, 'id'>[]) => {
    if (Array.isArray(style)) {
      setStyleList(style);
    } else {
      setStyleList([style, ...styleList]);
    }
  }

  useEffect(() => {
    if (!agentTemplateManager.getStyleList().length) {
      search();
    }
  }, [])

  useEffect(() => {
    const unsubscribe = agentTemplateManager.onStyleListChange((e) => {
      setStyleList(e.detail.styleList || [])
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = agentTemplateManager.onStyleSearchFormChange((e) => {
      setStyleSearchFormData(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    loading,
    styleList,
    styleSearchFormData,

    search,
    changeSearchForm,
    addStyleList,
    resetAndSearch,
  }
}

// 样式所有列表数据
const useStyleAllListData = (externalParams?: object) => {
  const toast = useToast();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);

  const [styleAllList, setStyleAllList] = useState(agentTemplateManager.getStyleAllList());
  const [styleAllSearchFormData, setStyleAllSearchFormData] = useState(agentTemplateManager.getStyleAllSearchForm());

  const search = async (page?: number, clear = true) => {
    if (page === 1 && clear) setStyleAllList([]);
    setLoading(true);
    try {
      await agentTemplateManager.fetchStyleAllList(page, externalParams);
    } catch (error) {
      toast.showToastByCode(error);
    }
    setLoading(false);
  }

  const changeSearchForm = (key: keyof StyleSearchForm, value: string | number) => {
    agentTemplateManager.setStyleAllSearchForm({
      ...styleAllSearchFormData,
      [key]: value,
    });
  }

  const changePage = (page: number) => {
    changeSearchForm('page', page);
  }

  const addStyleAllList = (style: StyleData) => {
    setStyleAllList([style, ...styleAllList]);
  }

  const resetAndSearch = async () => {
    agentTemplateManager.setStyleAllSearchForm({
      page: 1,
      pageSize: 10,
      total: 0,
      styleId: "",
    });
    await search();
  }

  useEffect(() => {
    const unsubscribe = agentTemplateManager.onStyleAllListChange((e) => {
      setStyleAllList(e.detail.styleAllList || [])
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = agentTemplateManager.onStyleAllSearchFormChange((e) => {
      setStyleAllSearchFormData(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    loading,
    styleAllList,
    styleAllSearchFormData,

    search,
    changePage,
    resetAndSearch,
    changeSearchForm,
  }
}

// 保存智能体模板
export const useSaveAgentTemplate = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const { id, userId } = useParams();
  const [loading, setLoading] = useState(false);
  const _userId = new URLSearchParams(location.search).get('userId');
  const { account } = iamAccountHooks.useData();

  type SaveRequest = Omit<AgentV2SaveTemplateRequest, 'agentId' | 'userId' | 'type'>

  const save = async (
    data: SaveRequest | SaveRequest[],
    options?: { userId?: string, agentId?: string, type?: number, autoRefresh?: boolean }
  ) => {
    try {
      // 外部配置参数
      const external = { autoRefresh: true, ...options };

      setLoading(true);

      // 处理单条数据和多条数据的情况
      const requests = Array.isArray(data) ? data : [data];

      // 文章、图片类型，前端增加重复校验
      if (
        userManagementManager.getActiveSettingTab() === SETTING_TABS.article ||
        userManagementManager.getActiveSettingTab() === SETTING_TABS.image
      ) {
        // 根据ids，过滤掉 templateList 中存在的项
        const validIds = requests.map(item => item.styleId)
          .filter(id => !agentTemplateManager.getTemplateList().some(template => template.uuid === id));

        if (validIds.length === 0) {
          toast.toast({
            description: t('agent_dashboard.user_detail.template.add_modal.save_valid_tips')
          });
          return;
        }
      }

      // 合并 userId 和 type
      const buildParams = requests.map((item) => ({
        ...item,
        userId: external?.userId || _userId,
        type: external?.type || PUSH_SETTINGS_TYPE[userManagementManager.getActiveSettingTab()],
      }))

      await agentTemplateManager.saveTemplate(buildParams, external?.agentId);

      // 刷新模板列表
      if (external?.autoRefresh) {
        const userIdValue = external?.userId || _userId || account?.id;
        const agentIdValue = external?.agentId || id
        if (!userIdValue || !agentIdValue) return;
        agentTemplateManager.fetchTemplateList({
          agentId: agentIdValue,
          userId: userIdValue,
        });
      }
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  return {
    loading,
    save,
  }
}

// 更新智能体模板
export const useUpdateAgentTemplate = () => {
  const toast = useToast();
  const { id, userId } = useParams();
  const [loading, setLoading] = useState(false);
  const _userId = new URLSearchParams(location.search).get('userId');

  const update = async (
    data: {
      uuid: string, styleId?: string, coverUrl?: string, coverVideo?: string, actorModelId?: string, voiceModelId?: string, speedRatio?: number, templateName?: string,
    },
    options?: {
      userId?: string, agentId?: string, type?: number, autoRefresh?: boolean
    }
  ) => {
    // 外部配置参数
    const external = { autoRefresh: true, ...options };

    try {
      setLoading(true);
      await agentTemplateManager.updateTemplate({
        ...data,
        userId: external?.userId || _userId,
        type: external?.type || PUSH_SETTINGS_TYPE[userManagementManager.getActiveSettingTab()],
      }, external?.agentId);

      // 刷新模板详情和列表
      if (external?.autoRefresh) {
        await Promise.all([
          agentTemplateManager.fetchTemplateDetail({
            uuid: data.uuid,
          }),
        ]);
        agentTemplateManager.fetchTemplateList({})
      }

    } catch (error) {
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  return {
    loading,
    update,
  }
}

// 获取智能体模板详情
export const useTemplateDetail = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const [templateDetail, setTemplateDetail] = useState(agentTemplateManager.getTemplateDetail());

  const fetchDetail = async (uuid: string) => {
    try {
      setLoading(true);
      return await agentTemplateManager.fetchTemplateDetail({
        uuid,
      });
    } catch (error) {
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const resetDetail = () => {
    agentTemplateManager.setTemplateDetail({} as AgentV2TemplateDetailData);
  }

  useEffect(() => {
    const unsubscribe = agentTemplateManager.onTemplateDetailChange((e) => {
      setTemplateDetail(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    loading,
    templateDetail,
    fetchDetail,
    resetDetail,
  }
}

// 删除智能体模板
export const useDeleteAgentTemplate = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const _userId = new URLSearchParams(location.search).get('userId');

  const deleteTemplate = async (
    templateId: string,
    userId?: string,
    options?: { agentId?: string, autoRefresh?: boolean }
  ) => {
    try {
      const external = { autoRefresh: true, ...options };
      setLoading(true);
      await agentTemplateManager.deleteTemplate({
        templateId,
        userId: userId || _userId,
      }, external?.agentId);
      if (external.autoRefresh) {
        await agentTemplateManager.fetchTemplateList({});
      }
    } catch (error) {
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  return {
    loading,
    deleteTemplate,
  }
}

//========================= 图文模板 =============================
// 飞书模板列表数据（添加模板弹窗里面的列表数据）
export const useLarkImageTemplatesData = (externalParams?: object) => {
  const toast = useToast();
  const { id, userId } = useParams();
  const params = useParams();

  const [larkImageTemplates, setLarkImageTemplates] = useState(agentTemplateManager.getLarkImageTemplates());
  const [larkImageTemplateSearchFormData, setLarkImageTemplateSearchFormData] = useState(agentTemplateManager.getLarkImageTemplateSearchForm());
  const [loading, setLoading] = useState(false);

  const search = async (page?: number, templateId?: string) => {
    setLoading(true);
    try {
      await agentTemplateManager.fetchLarkImageTemplates(externalParams);
    } catch (error) {
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const changeSearchForm = (key: keyof LarkImageTemplateSearchForm, value: string | number) => {
    agentTemplateManager.setLarkImageTemplateSearchForm({
      ...larkImageTemplateSearchFormData,
      [key]: value,
    });
  }

  useEffect(() => {
    // search();
  }, []);

  const addLarkImageTemplates = (template: LarkImageTableItemsData | LarkImageTableItemsData[]) => {
    if (Array.isArray(template)) {
      setLarkImageTemplates(template);
    } else {
      setLarkImageTemplates([template, ...larkImageTemplates]);
    }
  }

  const resetAndSearch = async (isSearch?: boolean) => {
    agentTemplateManager.setLarkImageTemplateSearchForm({
      pageSize: '100',
      pageToken: "",
      templateId: "",
    });
    setLarkImageTemplates([]);
    isSearch && await search();
  }

  useEffect(() => {
    const unsubscribe = agentTemplateManager.onLarkImageTemplatesChange((e) => {
      setLarkImageTemplates(e.detail)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = agentTemplateManager.onLarkImageTemplateSearchChange((e) => {
      setLarkImageTemplateSearchFormData(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    loading,
    larkImageTemplates,
    larkImageTemplateSearchFormData,

    changeSearchForm,
    search,
    resetAndSearch,
    addLarkImageTemplates,
  }
}


// ========================== 文章模板 ==========================
// 飞书模板列表数据（添加模板弹窗里面的列表数据）
export const useLarkArticleTemplatesData = (externalParams?: object) => {
  const toast = useToast();
  const { id, userId } = useParams();
  const params = useParams();

  const [larkArticleTemplates, setLarkArticleTemplates] = useState(agentTemplateManager.getLarkArticleTemplates());
  const [larkArticleTemplateSearchFormData, setLarkArticleTemplateSearchFormData] = useState(agentTemplateManager.getLarkArticleTemplateSearchForm());
  const [loading, setLoading] = useState(false);

  const search = async (page?: number, templateId?: string) => {
    setLoading(true);
    try {
      await agentTemplateManager.fetchLarkArticleTemplates(externalParams);
    } catch (error) {
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const changeSearchForm = (key: keyof LarkArticleTemplateSearchForm, value: string | number) => {
    agentTemplateManager.setLarkArticleTemplateSearchForm({
      ...larkArticleTemplateSearchFormData,
      [key]: value,
    });
  }

  useEffect(() => {
    // search();
  }, []);

  const addTemplateList = (template: LarkArticleTableItemsData | LarkArticleTableItemsData[]) => {
    if (Array.isArray(template)) {
      setLarkArticleTemplates(template);
    } else {
      setLarkArticleTemplates([template, ...larkArticleTemplates]);
    }
  }

  const resetAndSearch = async (isSearch?: boolean) => {
    agentTemplateManager.setLarkArticleTemplateSearchForm({
      pageSize: '100',
      pageToken: "",
      templateId: "",
    });
    setLarkArticleTemplates([]);
    isSearch && await search();
  }

  useEffect(() => {
    const unsubscribe = agentTemplateManager.onLarkArticleTemplatesChange((e) => {
      setLarkArticleTemplates(e.detail)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = agentTemplateManager.onLarkArticleTemplateSearchChange((e) => {
      setLarkArticleTemplateSearchFormData(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    loading,
    larkArticleTemplates,
    larkArticleTemplateSearchFormData,

    changeSearchForm,
    search,
    resetAndSearch,
  }
}


export const agentTemplateHooks = {
  // 视频模板
  useTemplateListData,
  useTemplateDetail,
  useSaveAgentTemplate,
  useDeleteAgentTemplate,
  useUpdateAgentTemplate,
  useStyleListData,
  useStyleAllListData,

  // 图文模板
  useLarkImageTemplatesData,

  // 文章模板
  useLarkArticleTemplatesData,
}

// 导出单例实例
export const agentTemplateManager = AgentTemplateManager.getInstance();
